import { DatePipe } from "@angular/common";
import { Component, OnInit, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { DataTable } from "primeng/primeng";
import { ObjectiveType } from "../../../shared/models/objective-type";
import { RequestForm } from "../../../shared/models/request-form";
import { RequestStatus } from "../../../shared/models/request-status";
import { RequestedType } from "../../../shared/models/requested-type";
import { convertISODateToDate } from "../../../shared/utilities.functions";
import { RequestListService } from "./request-list.service";

@Component({
  selector: "app-request-list",
  templateUrl: "./request-list.component.html",
  styleUrls: ["./request-list.component.css"]
})
export class RequestListComponent implements OnInit {
  loading: boolean;
  requestFormList: RequestForm[];
  requestedType;
  requestStatus;
  objectiveType;

  @ViewChild(DataTable)
  private dataTable: DataTable;

  constructor(
    private requestListService: RequestListService,
    private translate: TranslateService,
    private router: Router,
    private datePipe: DatePipe
  ) { }

  ngOnInit() {
    this.requestedType = RequestedType;
    this.requestStatus = RequestStatus;
    this.objectiveType = ObjectiveType;
    this.loading = true;
    this.requestFormList = [];
    this.requestListService.listRequestForm().subscribe(
      (requestFormList: RequestForm[]) => {
        this.requestFormList = requestFormList;
        this.requestFormList.forEach(v => {
          v.startDateTime = convertISODateToDate(v.startDateTime as string);
          v.endDateTime = convertISODateToDate(v.endDateTime as string);
        });
        this.loading = false;
      },
      error => {
        console.log("Error while loading request form list");
        console.log(error);
        console.log(error.error);
        this.loading = false;
      }
    );
  }

  onFilter(event) {
    this.dataTable.filter(event.value, event.columnName, event.filterType);
  }

  ngAfterViewChecked() {
    // Add custom dataTable filter constraints
    if (this.dataTable !== undefined) {
      const customFilterConstraints = this.dataTable.filterConstraints;
      customFilterConstraints["between"] = this.between;
      customFilterConstraints["containsObject"] = this.containsObject;
      customFilterConstraints["objectId"] = this.objectId;
      this.dataTable.filterConstraints = customFilterConstraints;
    }
  }

  between(value: any, filter: any): boolean {
    if (!filter || (!filter["from"] && !filter["to"])) return true;
    if (value == null) return false;
    if (filter) {
      let isBetween = true;
      let dateValue = new Date(value);
      if (filter["from"]) {
        isBetween = dateValue.getTime() >= filter["from"].getTime();
      }
      if (filter["to"]) {
        let dateTo: Date = new Date(filter["to"].getTime());
        dateTo.setDate(dateTo.getDate() + 1);
        isBetween = isBetween && dateValue <= dateTo;
      }
      return isBetween;
    } else {
      return true;
    }
  }

  containsObject(values: any[], filter: any): boolean {
    if (!filter || filter.id == null) return true;
    if (values == null || values.length == 0) return false;
    let isContain = false;
    values.forEach(value => {
      if (value.id === filter.id) {
        isContain = true;
      }
    });
    return isContain;
  }

  objectId(value: any, filter, any): boolean {
    if (!filter) return true;
    if (value == null || value.id == null) return false;
    return value.id == filter;
  }

  onRowSelect(selectedRequestForm) {
    this.router.navigate(["/request-form", selectedRequestForm.id]);
  }

  exportData(value, filteredValue, columns) {
    this.loading = true;
    let data = filteredValue || value;
    let csv = "\ufeff";
    let csvSeparator = ",";
    let exportFilename = "request_form_list";

    //headers
    for (let i = 0; i < columns.length; i++) {
      let column = columns[i];
      if (column.exportable && column.field) {
        csv += '"' + (column.header || column.field) + '"';
        if (i < columns.length - 1) {
          csv += csvSeparator;
        }
      }
    }

    // get text from translation
    this.translate
      .get([
        "objective_type.100",
        "objective_type.101",
        "objective_type.102",
        "objective_type.103",
        "objective_type.104",
        "objective_type.105",
        "objective_type.106"
      ])
      .subscribe(translations => {
        //body
        for (const element of data) {
          let record = element;
          let requestId = record.id;
          let company =
            (record.company.symbol ? record.company.symbol + " : " : "") +
            record.company.nameEn;
          let startDateTime = this.datePipe.transform(
            record.startDateTime,
            "dd/MM/yyyy HH:mm"
          );
          let requestedType = RequestedType[record.requestedType];
          let objectiveType =
            translations[
            "objective_type." + ObjectiveType[record.objectiveType]
            ];
          let locations = "";
          for (const element of record.locations) {
            locations += element.name + "\n";
          }
          let requestStatus = RequestStatus[record.requestStatus];
          let actualAccessDate =
            (record.actualAccessDate
              ? this.datePipe.transform(
                record.actualAccessDate,
                "dd/MM/yyyy HH:mm"
              )
              : "") +
            " " +
            (record.actualLeaveDate
              ? "- " +
              this.datePipe.transform(
                record.actualLeaveDate,
                "dd/MM/yyyy HH:mm"
              )
              : "");
          let lastUpdate = this.datePipe.transform(
            record.lastUpdate,
            "dd/MM/yyyy HH:mm"
          );
          csv += "\n";
          csv += this.convertToCsvText(requestId, csvSeparator);
          csv += this.convertToCsvText(company, csvSeparator);
          csv += this.convertToCsvText(startDateTime, csvSeparator);
          csv += this.convertToCsvText(requestedType, csvSeparator);
          csv += this.convertToCsvText(objectiveType, csvSeparator);
          csv += this.convertToCsvText(locations, csvSeparator);
          csv += this.convertToCsvText(requestStatus, csvSeparator);
          csv += this.convertToCsvText(actualAccessDate, csvSeparator);
          csv += this.convertToCsvText(lastUpdate, csvSeparator);
        }

        // write file and download
        let blob = new Blob([csv], {
          type: "text/csv;charset=utf-8;"
        });
        if (window.navigator['msSaveOrOpenBlob']) {
          navigator['msSaveOrOpenBlob'](blob, exportFilename + ".csv");
        } else {
          let link = document.createElement("a");
          link.style.display = "none";
          document.body.appendChild(link);
          if (link.download !== undefined) {
            link.setAttribute("href", URL.createObjectURL(blob));
            link.setAttribute("download", exportFilename + ".csv");
            link.click();
          } else {
            csv = "data:text/csv;charset=utf-8," + csv;
            window.open(encodeURI(csv));
          }
          document.body.removeChild(link);
        }
        this.loading = false;
      });
  }

  convertToCsvText(data, csvSeparator) {
    if (!data) data = "";
    return '"' + data + '"' + csvSeparator;
  }
}



// WEBPACK FOOTER //
// /app/src/app/modules/request/request-list/request-list.component.ts